import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import useAxios from "../../utils/useAxios";
import { CustomModal } from "../../components/CustomModel/CustomModal";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";

const SuperAdminUserDetailModal = ({ onUserUpdate }) => {
    const { t } = useTranslation();
    const api = useAxios();
    const history = useHistory();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const adminId = query.get("admin_id");

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [availableClubs, setAvailableClubs] = useState([]);
    const [selectedClubIds, setSelectedClubIds] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [clubsNextPage, setClubsNextPage] = useState(null);
    const [loadingClubs, setLoadingClubs] = useState(false);

    const { ref: loadMoreRef, inView } = useInView({ threshold: 0.1 });

    const fetchClubs = async (reset = false, searchStr = "") => {
        setLoadingClubs(true);
        try {
            const url = reset
                ? `/api/v1.0/clubs/?is_intennis_club=true${searchStr ? `&search=${encodeURIComponent(searchStr)}` : ""}`
                : clubsNextPage;

            if (!url) return;

            const res = await api.get(url);
            setAvailableClubs(prev => reset ? (res.data.results || res.data) : [...prev, ...(res.data.results || [])]);
            setClubsNextPage(res.data.next ? res.data.next.replace(/^https?:\/\/[^/]+/, '') : null);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingClubs(false);
        }
    };

    const debouncedClubSearch = useMemo(
        () => debounce((val) => {
            setSearchQuery(val);
            fetchClubs(true, val);
        }, 400),
        []
    );

    useEffect(() => {
        return () => debouncedClubSearch.cancel();
    }, [debouncedClubSearch]);

    useEffect(() => {
        if (!adminId) {
            setUser(null);
            setAvailableClubs([]);
            setSearchQuery("");
            setClubsNextPage(null);
            return;
        }

        setLoading(true);
        api.get(`/api/v1.0/admin-users/${adminId}/`)
            .then((userRes) => {
                setUser(userRes.data);
                setSelectedClubIds(new Set(userRes.data.intennis_clubs || []));
                fetchClubs(true, "");
            })
            .catch(() => {
                toast.error(t("User not found"));
                handleClose();
            })
            .finally(() => setLoading(false));
    }, [adminId]);

    useEffect(() => {
        if (inView && clubsNextPage && !loadingClubs) {
            fetchClubs(false, searchQuery);
        }
    }, [inView, clubsNextPage, loadingClubs, searchQuery]);

    const handleClose = () => {
        const newQuery = new URLSearchParams(location.search);
        newQuery.delete("admin_id");
        history.push({ search: newQuery.toString() });
    };

    const handleToggleClub = (clubId) => {
        setSelectedClubIds(prev => {
            const next = new Set(prev);
            if (next.has(clubId)) next.delete(clubId);
            else next.add(clubId);
            return next;
        });
    };

    const handleToggleAdmin = (checked) => {
        setUser(prev => ({ ...prev, is_admin: checked }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch(`/api/v1.0/admin-users/${adminId}/`, {
                is_admin: user.is_admin,
                intennis_clubs: Array.from(selectedClubIds)
            });
            toast.success(t("Settings saved successfully"));
            if (onUserUpdate) onUserUpdate();
            handleClose();
        } catch (err) {
            toast.error(t("Failed to save changes"));
        } finally {
            setSaving(false);
        }
    };

    if (!adminId) return null;

    return (
        <CustomModal isOpen={!!adminId} onClose={handleClose}>
            {loading ? (
                <div style={{ padding: "20px" }}>
                    <Skeleton height={200} />
                </div>
            ) : user ? (
                <form className="modal-form" onSubmit={handleSave}>
                    <div className="modal-sticky-header">
                        <h2>
                            <FeatherIcon icon="shield" /> {t("Manage Admin Role")}
                        </h2>

                        <table style={{ width: "100%", marginTop: "15px" }}>
                            <tbody>
                            <tr>
                                <td style={{ width: "110px", verticalAlign: "middle" }}>
                                    <div className="no-thumbnail big"><FeatherIcon icon="user" /></div>
                                </td>
                                <td style={{ verticalAlign: "top" }}>
                                    <table className="player_modal" style={{ width: "100%" }}>
                                        <tbody>
                                        <tr>
                                            <td><label>{t("Name")}</label></td>
                                            <td>
                                                <span className="uppercase">
                                                    {user.last_name}
                                                </span>, {user.first_name}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><label>{t("Email")}</label></td>
                                            <td>{user.email}</td>
                                        </tr>
                                        <tr>
                                            <td><label>{t("Is Admin")}</label></td>
                                            <td>
                                                <label>
                                                    <input
                                                        style={{float: "right"}}
                                                        type="checkbox"
                                                        checked={user.is_admin}
                                                        onChange={(e) => handleToggleAdmin(e.target.checked)}
                                                    />
                                                </label>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div>
                        <label>
                            {t("Assigned Intennis Clubs")}
                        </label>
                        <div style={{ position: "relative", flex: 1 }}>
                            <FeatherIcon icon="search" size={16} className="search-icon" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--secondary-color)" }} />
                            <input
                                type="text"
                                className="search"
                                placeholder={t("Search clubs...")}
                                style={{ paddingLeft: "35px", width: "100%", paddingRight: "10px" }}
                                onChange={(e) => debouncedClubSearch(e.target.value)}
                            />
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "30px", maxHeight: "600px", overflowY: "auto", overflowX: "hidden", padding: "5px" }}>
                            {availableClubs.map(club => (
                                <label key={club.id} style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", padding: "10px", background: "var(--body-background)", borderRadius: "8px" }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedClubIds.has(club.id)}
                                        onChange={() => handleToggleClub(club.id)}
                                        style={{ transform: "scale(1.2)", flexShrink: 0 }}
                                    />
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{club.name}</span>
                                </label>
                            ))}

                            {loadingClubs && Array.from({ length: 4 }).map((_, idx) => (
                                <div key={`skel-${idx}`} style={{ padding: "10px", background: "var(--body-background)", borderRadius: "8px", height: "42px" }}>
                                    <Skeleton height={20} />
                                </div>
                            ))}

                            {!loadingClubs && availableClubs.length === 0 && (
                                <div style={{ color: "var(--secondary-color)", gridColumn: "span 2", textAlign: "center" }}>{t("No Intennis clubs available.")}</div>
                            )}

                            {clubsNextPage && !loadingClubs && (
                                <div ref={loadMoreRef} style={{ gridColumn: "span 2", height: "20px" }}></div>
                            )}
                        </div>

                        <ul className="tabs">
                            <li>
                                <button type="submit" className="action-button active" disabled={saving || loadingClubs}>
                                    {saving ? t("Saving...") : t("Save")}
                                </button>
                            </li>
                            <li>
                                <button type="button" className="action-button" onClick={handleClose}>
                                    {t("Cancel")}
                                </button>
                            </li>
                        </ul>
                    </div>
                </form>
            ) : null}
        </CustomModal>
    );
};

export default SuperAdminUserDetailModal;
